/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import {
    DEFAULT_GUIDE_TASKS,
    DEFAULT_LANDING_LOCATION_URL,
    DEFAULT_LANDING_SCREEN,
} from '../../config';
import { KeyManager } from '../../core/navigation';
import { LocationUrls } from '../../core/navigation/LocationUrls';
import { ScreenController } from '../../core/navigation/ScreenController';
import { ScreenControllerTypes } from '../../core/navigation/ScreenControllerTypes';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { TScreen } from '../../core/navigation/types';
import { TListViewType } from '../../core/types';
import { setLocationUrl } from '../urlchangeevents/urlchangeevents.reducers';
import { setScreenControllerAction, setScreenTaskControllerAction } from './screens.actions';
import { SET_SCREEN_CONTROLLER, SET_SCREEN_TASK_CONTROLLER } from './screens.types';

const initialState: {
    screenController: string;
    screenTaskController: string;
    previousScreenController: string;
    previousScreenTaskController: string;
} = {
    screenController: ScreenControllerTypes.SPLASH_SCREEN,
    screenTaskController: ScreenTaskControllerTypes.NO_TASK,
    previousScreenController: ScreenControllerTypes.SPLASH_SCREEN,
    previousScreenTaskController: ScreenTaskControllerTypes.NO_TASK,
};

const screensReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_SCREEN_CONTROLLER:
            return {
                ...state,
                screenController: payload.screenController,
                previousScreenController: payload.previousScreenController,
            };

        case SET_SCREEN_TASK_CONTROLLER:
            return {
                ...state,
                screenTaskController: payload.screenTaskController,
                previousScreenTaskController: payload.previousScreenTaskController,
            };
        default:
            return state;
    }
};

export const setScreenController =
    (screenController: string) => (dispatch: Function, getState: Function) => {
        const { screenController: previousScreenController } = getState().screens;
        dispatch(setScreenControllerAction(screenController, previousScreenController));
    };

export const setScreenTaskController =
    (screenTaskController: string) => (dispatch: Function, getState: Function) => {
        const { screenTaskController: previousScreenTaskController } = getState().screens;
        dispatch(setScreenTaskControllerAction(screenTaskController, previousScreenTaskController));
    };

export const showAccountScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.ACCOUNT_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.ACCOUNT_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_ACCOUNT_TASKS));
};

export const showCloudDvrHelpScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.CLOUD_DVR_HELP_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.CLOUD_DVR_HELP_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showPCHelpScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.PC_HELP_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.PC_HELP_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showPCOnOffHelpScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.PC_HELP_CONTROLS_ON_OFF));
    dispatch(setScreenController(ScreenControllerTypes.PC_HELP_SCREEN_OPTION));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showPCLocksLimitsHelpScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.PC_HELP_LOCKS_LIMITS));
    dispatch(setScreenController(ScreenControllerTypes.PC_HELP_SCREEN_OPTION));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showPCForgottenPinHelpScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.PC_HELP_FORGOTTEN_PIN));
    dispatch(setScreenController(ScreenControllerTypes.PC_HELP_SCREEN_OPTION));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showDefaultScreen = () => (dispatch: Function) => {
    dispatch(setLocationUrl(DEFAULT_LANDING_LOCATION_URL));
    dispatch(setScreenController(DEFAULT_LANDING_SCREEN));
    dispatch(setScreenTaskController(DEFAULT_GUIDE_TASKS));
};

export const showEmptyGuideScreen = () => (dispatch: Function) => {
    dispatch(setLocationUrl(LocationUrls.EMPTY_GUIDE));
    dispatch(setScreenController(ScreenControllerTypes.EMPTY_GUIDE_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const showErrorScreen = () => (dispatch: Function) => {
    dispatch(setLocationUrl(LocationUrls.ERROR_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.ERROR_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_ERROR_TASKS));
};

export const showGalleryScreen = (parentVodItemId: string) => (dispatch: Function) => {
    dispatch(setLocationUrl(`${LocationUrls.GALLERY_SCREEN}/${parentVodItemId}`));
    dispatch(setScreenController(ScreenControllerTypes.GALLERY_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS));
};

export const showGuideScreen = () => (dispatch: Function) => {
    ScreenController.getInstance().emptyScreenControllerStack();
    dispatch(setLocationUrl(LocationUrls.GUIDE));
    dispatch(setScreenController(ScreenControllerTypes.GUIDE));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const showFilteredGuideScreen = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;
    KeyManager.getInstance().startKeyIgnoring();
    if (screenController !== ScreenControllerTypes.FILTERED_GUIDE_SCREEN) {
        // This case is valid when changing the filter values for the category pill from the filtered guide
        dispatch(setLocationUrl(LocationUrls.FILTERED_GUIDE));
        dispatch(setScreenController(ScreenControllerTypes.FILTERED_GUIDE_SCREEN));
    }
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const showEmptyFilteredGuideScreen = () => (dispatch: Function) => {
    dispatch(setLocationUrl(LocationUrls.EMPTY_FILTERED_GUIDE));
    dispatch(setScreenController(ScreenControllerTypes.EMPTY_FILTERED_GUIDE_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const showHelpScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.HELP_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.HELP_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showHideAdultContentScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.PC_HIDE_ADULT_CONTENT_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN));
    dispatch(
        setScreenTaskController(
            ScreenTaskControllerTypes.SETTINGS_PC_HIDE_ADULT_CONTENT_DEFAULT_TASKS,
        ),
    );
};

export const showLiveTvScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.VIDEO_PLAYER));
    dispatch(setScreenController(ScreenControllerTypes.VIDEO_PLAYER));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS));
};

export const showMenuLanguageScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.MENU_LANGUAGE_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.MENU_LANGUAGE_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showListViewScreen =
    (ListViewScreenInfo: {
        id: string;
        title: string;
        listType: TListViewType;
        isAdult: boolean;
        collapsedVodItemId?: string;
        partnerId?: string;
    }) =>
    (dispatch: Function) => {
        KeyManager.getInstance().startKeyIgnoring();
        const queryString = new URLSearchParams(
            // eslint-disable-next-line no-unused-vars
            Object.entries(ListViewScreenInfo)
                .filter(([_, v]) => v !== undefined)
                .reduce<Record<string, string>>((acc, [k, v]) => {
                    acc[k] = String(v);
                    return acc;
                }, {}),
        ).toString();
        dispatch(setLocationUrl(`${LocationUrls.LIST_VIEW_SCREEN}?${queryString}`));
        dispatch(setScreenController(ScreenControllerTypes.LIST_VIEW_SCREEN));
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK));
    };

export const showMyShowsScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.MY_SHOWS_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.MY_SHOWS_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS));
};

export const showCollectionScreen =
    (collectionScreenInfo: {
        unifiedCollectionId: string;
        transitionScreen: string;
        partnerId?: string;
        collapsedVodItemId?: string;
        contentSupplierPartnerId?: string;
    }) =>
    (dispatch: Function) => {
        KeyManager.getInstance().startKeyIgnoring();
        const queryString = new URLSearchParams(
            // eslint-disable-next-line no-unused-vars
            Object.entries(collectionScreenInfo)
                .filter(([_, v]) => v !== undefined)
                .reduce<Record<string, string>>((acc, [k, v]) => {
                    acc[k] = String(v);
                    return acc;
                }, {}),
        ).toString();
        dispatch(setLocationUrl(`${LocationUrls.COLLECTION_SCREEN}?${queryString}`));
        dispatch(setScreenController(ScreenControllerTypes.COLLECTION_SCREEN));
        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_COLLECTION_SCREEN_TASKS),
        );
    };

export const showInfoScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.INFO_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.INFO_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showManageScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.MANAGE_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.MANAGE_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showMainSettingsScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.MAIN_SETTINGS_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.MAIN_SETTINGS_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS));
};

export const showMyShowsOptionsMenu = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.MY_SHOWS_OPTIONS_MENU_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showOnepassAndRecordingsMenu = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.ONEPASS_AND_RECORDINGS_OPTIONS_MENU_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showOnePassManagerScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.ONEPASS_MANAGER_SETTINGS_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.ONEPASS_MANAGER_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.ONEPASS_MANAGER_DEFAULT_TASKS));
};

export const showToDoListScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.TODO_LIST_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.TODO_LIST_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showEmptyToDoListScreen = () => (dispatch: Function) => {
    // Remove the normal to do list page screen from the history stack, otherwise when
    // navigating back from the manage screen, the user will land on the normal to do list
    // page screen instead of the settings manage screen.
    // This method should be used only from ToDoList context
    ScreenController.getInstance().onKeyBack();
    dispatch(setLocationUrl(LocationUrls.EMPTY_TODO_LIST_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.EMPTY_TODO_LIST_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showRecordingActivityScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.RECORDING_ACTIVITY_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.RECORDING_ACTIVITY_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showEmptyRecordingActivityScreen = () => (dispatch: Function) => {
    // Remove the normal recording activity page screen from the history stack, otherwise when
    // navigating back from the manage screen, the user will land on the normal recording activity
    // page screen instead of the settings manage screen.
    // This method should be used only from RecordingActivity context
    ScreenController.getInstance().onKeyBack();
    dispatch(setLocationUrl(LocationUrls.EMPTY_RECORDING_ACTIVITY_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.EMPTY_RECORDING_ACTIVITY_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showPCScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.PC_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.PC_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.PC_DEFAULT_TASKS));
};

export const showPreviousScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    ScreenController.getInstance().onKeyBack();
    const currentScreen: TScreen = <TScreen>(
        ScreenController.getInstance().getScreen().getProperties()
    );
    dispatch(setLocationUrl(currentScreen.url));
    dispatch(setScreenController(currentScreen.screenController));
    dispatch(setScreenTaskController(currentScreen.taskController));
};

export const showProgramScreen = () => (dispatch: Function) => {
    // append current time to the screen identifier to simulate a new screen
    dispatch(setLocationUrl(`${LocationUrls.PROGRAM_SCREEN}/${Date.now()}`));
    dispatch(setScreenController(ScreenControllerTypes.PROGRAM_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS));
};

export const showSetRatingLimitsScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.PC_SET_RATING_LIMITS));
    dispatch(setScreenController(ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.PC_SET_RATING_LIMITS_DEFAULT_TASKS));
};

export const showSignInScreen = () => (dispatch: Function) => {
    dispatch(setLocationUrl(LocationUrls.SIGN_IN_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.SIGN_IN_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_SIGN_IN_TASKS));
};

export const showSplashScreen = () => (dispatch: Function) => {
    dispatch(setLocationUrl(LocationUrls.SPLASH_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.SPLASH_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showTopScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    const currentScreen: TScreen = <TScreen>(
        ScreenController.getInstance().getScreen().getProperties()
    );
    dispatch(setLocationUrl(currentScreen.url));
    dispatch(setScreenController(currentScreen.screenController));
    dispatch(setScreenTaskController(currentScreen.taskController));
};

export const showUserPreferencesScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.USER_PREFERENCES_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.USER_PREFERENCES_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.NO_TASK));
};

export const showUserPrefAutoplayNextEpisodeScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.USER_PREF_AUTOPLAY_NEXT_EPISODE_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.USER_PREF_AUTOPLAY_NEXT_EPISODE_SCREEN));
    dispatch(
        setScreenTaskController(ScreenTaskControllerTypes.USER_PREF_AUTOPLAY_NEXT_EPISODE_TASKS),
    );
};

export const showUserPrefVideoWindowScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.USER_PREF_VIDEO_WINDOW_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.USER_PREF_VIDEO_WINDOW_TASKS));
};

export const showVodBrowseMainScreen = () => (dispatch: Function) => {
    KeyManager.getInstance().startKeyIgnoring();
    dispatch(setLocationUrl(LocationUrls.VOD_BROWSE_MAIN_SCREEN));
    dispatch(setScreenController(ScreenControllerTypes.VOD_BROWSE_MAIN_SCREEN));
    dispatch(
        setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS),
    );
};

export default screensReducer;
